import { CatalystInsight, EarningsInsight } from '@api';
import { TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { CompanyCatalystCounterTooltip } from '@terminal/views/dashboard/my-assets-widget/assets-table/insights-cell/company-catalyst-counter-tooltip/CompanyCatalystCounterTooltip';
import { CatalystInsightIcon } from '@terminal/widgets/catalyst-insights-drawer/catalyst-insight-icon/CatalystInsightIcon';
import { EarningsInsightIcon } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-icon/EarningsInsightIcon';
import { useState } from 'react';

import styles from './Cell.module.scss';

interface CatalystsCellProps {
  variant?: 'dashboard' | 'themeOverview';
  entityTag?: string;
  insights: CatalystInsight[];
  earningsInsight?: EarningsInsight;
  source: PosthogTrackingSource;
}

export const CatalystsCell = ({
  variant = 'dashboard',
  entityTag,
  insights,
  earningsInsight,
  source,
}: CatalystsCellProps) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isThemeOverview = variant === 'themeOverview';
  const maxCatalysts = isThemeOverview ? 2 : 3;

  const visibleCatalysts = insights.slice(0, maxCatalysts);
  const remainingCatalysts = insights.slice(maxCatalysts);
  const containerClass = isThemeOverview
    ? styles.insightsCellFixed
    : styles.insightsCell;

  if (!entityTag && !insights.length) {
    return null;
  }

  return (
    <div className={containerClass}>
      {entityTag && earningsInsight ? (
        <EarningsInsightIcon
          entityTag={entityTag}
          earningsInsight={earningsInsight}
          width={18}
          height={20}
          source={source}
        />
      ) : entityTag ? (
        <div className={styles.placeholder} />
      ) : null}
      {visibleCatalysts.map(insight => (
        <CatalystInsightIcon
          key={insight.id}
          customSize={{ width: 18, height: 20 }}
          enableHover
          isBordered
          catalystInsightId={insight.id}
          identifier={insight.identifier}
          category={insight.category}
          subCategory={insight.sub_category}
          source={source}
        />
      ))}
      {!!remainingCatalysts.length && (
        <TooltipContainer
          label={
            <CompanyCatalystCounterTooltip
              insights={remainingCatalysts}
              source={source}
              onClose={() => setIsTooltipOpen(false)}
            />
          }
          inPortal
          trigger={[TooltipTrigger.Hover]}
          placement="top-end"
          withSafePolygon
          isOpen={isTooltipOpen}
          onOpen={() => setIsTooltipOpen(true)}
          onClose={() => setIsTooltipOpen(false)}
        >
          <span className={styles.moreCount}>+{remainingCatalysts.length}</span>
        </TooltipContainer>
      )}
    </div>
  );
};
